import React, { FC, useEffect, useState, useContext } from "react"
import { Form, Select, Button, Table, Space, Col, Row } from "antd"
import type { ColumnsType } from 'antd/es/table';
import { statisticsOfTag, statisticsOfType, exportStatisticsOfTag, exportStatisticsOfType, selectTag } from "@root/common/service";
import { YearSelect, MonthSelect } from '@root/components/selectBox';
import { StatisticsContext, TotalViewProps, DateProps } from '../index';
const { Option } = Select;

// 按标签查询：列表内容数据类型
interface TagProps {
  averageWatchTime: number,
  courseCount: number,
  courseDuration: number,
  courseTag: string,
  index: string,
  watchTime: number,
  watchUserCount: number,
}

// 按类型查询：列表内容数据类型
interface TypeProps {
  averageWatchTime: number,
  courseCount: number,
  courseDuration: number,
  courseTag: string,
  index: string,
  ownerType: number,
  ownerTypeText: string,
  watchTime: number,
  watchUserCount: number
}

// 按标签查询props数据类型
interface TagViewProps {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setTagId: React.Dispatch<React.SetStateAction<string>>,
  getReport: (data: TotalViewProps | null) => string,
  setDate: React.Dispatch<React.SetStateAction<DateProps | undefined>>
}

// 按类型查询props数据类型
interface TypeViewProps {
  tagId: string,
  date: DateProps | undefined,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  getReport: (data: TotalViewProps | null) => string
}

// 按标签查询
const TagView: FC<TagViewProps> = (props) => {
  const { setOpen, setTagId, getReport, setDate } = props;

  const [form] = Form.useForm();
  const context = useContext(StatisticsContext);

  const [viewList, setViewList] = useState<TagProps[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableTotal, setTableTotal] = useState(0);
  const [total, setTotal] = useState<TotalViewProps | null>(null);
  const [courseTagList, setCourseTagList] = useState<{ id: number, tagName: string }[]>([]);

  const columns: ColumnsType<TagProps> = [
    {
      title: '序号',
      dataIndex: 'index',
      key: 'index',
    },
    {
      title: '课程标签',
      dataIndex: 'courseTag',
      key: 'courseTag',
      render: (text, tag) => {
        return (
          <Button
            type="link"
            onClick={() => { onTagClickHandle(tag) }}
          >{text}</Button>
        )
      }
    },
    {
      title: '课程数量',
      dataIndex: 'courseCount',
      key: 'courseCount',
    },
    {
      title: '课程时长',
      dataIndex: 'courseDuration',
      key: 'courseDuration',
    },
    {
      title: '学习人数',
      dataIndex: 'watchUserCount',
      key: 'watchUserCount',
    },
    {
      title: '学习时长/h',
      dataIndex: 'watchTime',
      key: 'watchTime',
    },
    {
      title: '平均学习时长/h',
      dataIndex: 'averageWatchTime',
      key: 'averageWatchTime',
    }
  ];

  /**
   * 获取列表内容
   * @param page 页码
   */
  const getCountryView = async (page = 1) => {
    const params = form.getFieldsValue();

    setTableLoading(true);
    statisticsOfTag(
      { pageNum: page, pageSize: 10 },
      { ...params }
    ).then(res => {
      const { data: { tableDataInfo, totalCourseTagView } } = res;
      setViewList(tableDataInfo.rows);
      setTableTotal(tableDataInfo.total);
      setTotal(totalCourseTagView)
    }).finally(() => {
      setTableLoading(false);
    });
  };

  // excel 导出
  const onExport = () => {
    let params = form.getFieldsValue();
    let uri = '?';

    if (context && context.getUrlParams) {
      uri += context.getUrlParams(params)
    }

    exportStatisticsOfTag(uri).then(res => {
      if (context && context.excelDownload) {
        context.excelDownload(res);
      }
    })
  }

  /**
   * 点击标签触发
   * @param tag 标签内容
   */
  const onTagClickHandle = (tag: TagProps) => {
    setDate({
      year: form.getFieldValue('year'),
      month: form.getFieldValue('month')
    })
    setTagId(tag.courseTag);
    setOpen(true);
  }

  useEffect(() => {
    getCountryView();

    selectTag().then((res) => {
      setCourseTagList(res.data);
    });
  }, []);

  return (
    <div className="coursestatistics-country">
      <div className="coursestatistics-form-title">
        <Form
          form={form}
          name="customized_form_controls"
          layout="inline"
          onFinish={() => { getCountryView() }}
        >
          <Row>
            <Col span={6}>
              <YearSelect
                label="年度"
                name="year"
              />
            </Col>
            <Col span={6}>
              <MonthSelect
                label="月份"
                name="month"
              />
            </Col>
            <Col span={6}>
              <Form.Item label="课程标签"
                name="courseTag"
              >
                <Select style={{ width: '100%' }}>
                  {
                    courseTagList.map((tag, index) => {
                      return <Option key={index} value={tag.tagName}>{tag.tagName}</Option>
                    })
                  }
                </Select>
              </Form.Item>
            </Col>
            <Col span={6} style={{ textAlign: 'right' }}>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    查询
                  </Button>
                  <Button type="primary" onClick={() => {
                    form.resetFields();
                    getCountryView()
                  }}>
                    重置
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>

        </Form>
      </div>
      <div className="coursestatistics-export">
        <Button type="primary" onClick={() => { onExport() }}>导出</Button>
      </div>
      <div className="coursestatistics-title">
        {getReport(total)}
      </div>
      <div className="coursestatistics-table">
        <Table
          rowKey="index"
          columns={columns}
          dataSource={viewList}
          loading={tableLoading}
          pagination={{
            total: tableTotal,
            onChange: (page) => { getCountryView(page) },
            showTotal: (total) => {
              return '共' + (total || 0) + '条记录';
            },
          }}
        />
      </div>
    </div>
  )
}

// 按类型查询
const TypeView: FC<TypeViewProps> = (props) => {
  const { date, tagId, getReport, setOpen } = props;

  const [form] = Form.useForm()
  const context = useContext(StatisticsContext);

  const [viewList, setViewList] = useState<TypeProps[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableTotal, setTableTotal] = useState(0);
  const [total, setTotal] = useState<TotalViewProps | null>(null);

  const columns: ColumnsType<TagProps> = [
    {
      title: '序号',
      dataIndex: 'index',
      key: 'index',
    },
    {
      title: '课程标签',
      dataIndex: 'courseTag',
      key: 'courseTag',
    },
    {
      title: '课程类型',
      dataIndex: 'ownerTypeText',
      key: 'ownerTypeText',
    },
    {
      title: '课程数量',
      dataIndex: 'courseCount',
      key: 'courseCount',
    },
    {
      title: '课程时长',
      dataIndex: 'courseDuration',
      key: 'courseDuration',
    },
    {
      title: '学习人数',
      dataIndex: 'watchUserCount',
      key: 'watchUserCount',
    },
    {
      title: '学习时长/h',
      dataIndex: 'watchTime',
      key: 'watchTime',
    },
    {
      title: '平均学习时长/h',
      dataIndex: 'averageWatchTime',
      key: 'averageWatchTime',
    }
  ];

  /**
   * 获取列表内容
   * @param page 页码
   */
  const getStatisticsOfType = async (page = 1) => {
    setTableLoading(true);
    const params = form.getFieldsValue();
    statisticsOfType(
      { pageNum: page, pageSize: 10 },
      { ...params, courseTag: tagId }
    ).then(res => {
      const { data: { tableDataInfo, totalCourseTagView } } = res;
      setViewList(tableDataInfo.rows);
      setTableTotal(tableDataInfo.total);
      setTotal(totalCourseTagView)
    }).finally(() => {
      setTableLoading(false);
    });
  };

  // 导出
  const onExport = () => {
    let params = form.getFieldsValue();
    let uri = '?courseTag=' + tagId;

    if (context && context.getUrlParams) {
      uri += context.getUrlParams(params)
    }

    exportStatisticsOfType(uri).then(res => {
      if (context && context.excelDownload) {
        context.excelDownload(res);
      }
    })
  }

  useEffect(() => {
    getStatisticsOfType();
  }, []);

  return (
    <div className="coursestatistics-country">
      <div className="coursestatistics-form-title">
        <Form
          form={form}
          name="customized_form_controls"
          layout="inline"
          onFinish={() => { getStatisticsOfType() }}
          initialValues={{
            year: date?.year,
            month: date?.month
          }}
        >
          <Row>
            <Col span={6}>
              <YearSelect
                label="年度"
                name="year"
              />
            </Col>
            <Col span={6}>
              <MonthSelect
                label="月份"
                name="month"
              />
            </Col>
            <Col span={12} style={{ textAlign: 'right' }}>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    查询
                  </Button>
                  <Button type="primary" onClick={() => { getStatisticsOfType() }}>
                    重置
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <div className="coursestatistics-export">
        <Space>
          <Button onClick={() => { setOpen(false) }}>返回</Button>
          <Button type="primary" onClick={onExport}>导出</Button>
        </Space>
      </div>
      <div className="coursestatistics-title">
        {getReport(total)}
      </div>
      <div className="coursestatistics-table">
        <Table
          rowKey="index"
          columns={columns}
          dataSource={viewList}
          loading={tableLoading}
          pagination={{
            total: tableTotal,
            onChange: (page) => { getStatisticsOfType(page) },
            showTotal: (total, range) => {
              return '共' + total || 0 + '条记录';
            },
          }}
        />
      </div>
    </div>
  )
}

const Country: FC = () => {
  // 设置是否为课程类型查询
  const [openTypeVis, setOpen] = useState<boolean>(false);
  // 设置标签id：用于下钻查询
  const [tagId, setTagId] = useState<string>('');
  // 月份与年份：用于下钻查询时赋值
  const [date, setDate] = useState<DateProps>()

  // 获取合计内容
  const getReport = (data: TotalViewProps | null) => {
    if (data) {
      return `合计: 课程数量：${data.courseCount}；课程时长/h：${data.courseDuration}；学习人数：${data.watchUserCount}；学习时长/h：${data.watchTime}；平均学习时长/h：${data.averageWatchTime}`
    } else {
      return ''
    }
  }

  return (
    <React.Fragment>
      {
        openTypeVis ?
          <TypeView
            tagId={tagId}
            date={date}
            setOpen={setOpen}
            getReport={getReport}
          />
          :
          <TagView
            setOpen={setOpen}
            setTagId={setTagId}
            getReport={getReport}
            setDate={setDate}
          />
      }

    </React.Fragment>
  )
}

export default Country;